/* InviteDialog — admin-only invitation creation modal. */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ROLE_LABEL, ROLE_DESCRIPTION } from '@/lib/employerAuthority';

// Owners cannot be invited — owner is auto-assigned at organisation
// creation. Admins/recruiters/reviewers are invitable.
const ROLES = ['admin', 'recruiter', 'reviewer'];

export function InviteDialog({ onClose, onSubmit }) {
  const [email, setEmail] = useState('');
  const [role,  setRole]  = useState('recruiter');
  const [busy,  setBusy]  = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await onSubmit({ targetEmail: email.trim(), targetRole: role });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="emp-invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite a member</DialogTitle>
          <DialogDescription>
            They will receive a pending invitation that expires in 7 days.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="emp-invite-email">Email address</Label>
            <Input
              id="emp-invite-email"
              type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@company.com"
              data-testid="emp-invite-email-input"
            />
          </div>
          <div>
            <Label>Role</Label>
            <RadioGroup value={role} onValueChange={setRole} className="mt-2 space-y-2"
              data-testid="emp-invite-role-radio">
              {ROLES.map((r) => (
                <Label key={r}
                  htmlFor={`emp-invite-role-${r}`}
                  className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm hover:bg-ndor-surface-alt dark:hover:bg-zinc-800 has-[[data-state=checked]]:bg-ndor-surface-alt dark:has-[[data-state=checked]]:bg-zinc-800 has-[[data-state=checked]]:border-zinc-300">
                  <RadioGroupItem value={r} id={`emp-invite-role-${r}`}
                    data-testid={`emp-invite-role-${r}`}
                    className="mt-0.5" />
                  <div className="space-y-0.5">
                    <div className="font-medium">{ROLE_LABEL[r]}</div>
                    <p className="text-xs text-slate-300 dark:text-slate-300 leading-relaxed">
                      {ROLE_DESCRIPTION[r]}
                    </p>
                  </div>
                </Label>
              ))}
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}
              data-testid="emp-invite-cancel">Cancel</Button>
            <Button type="submit" disabled={busy}
              data-testid="emp-invite-submit">
              {busy ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
